import React, { useState } from 'react';
import { LuLayers, LuPlus, LuTrash2, LuChevronUp, LuChevronDown, LuSparkles, LuEye } from 'react-icons/lu';
import MediaInput from './MediaInput.jsx';
import { resolveMediaUrl } from '../utils/styleHelpers.js';

export const PARALLAX_LAYER_PRESETS = [
    {
        id: 'cosmic',
        name: '🌌 3-Plane Cosmic Depth',
        description: 'Distant starfield, midground nebula, and floating space dust',
        layers: [
            {
                id: 'preset-sky-1',
                name: 'Deep Stars (Far / 0.12x)',
                image: 'https://images.unsplash.com/photo-1506703719100-a0f3a48c0f86?auto=format&fit=crop&w=1920&q=80',
                speed: 0.12,
                direction: 'vertical',
                opacity: 1,
                blendMode: 'normal',
                size: 'cover',
                position: 'center',
            },
            {
                id: 'preset-nebula-2',
                name: 'Midground Nebula Glow (0.38x)',
                image: 'https://images.unsplash.com/photo-1518709268805-4e9042af9f23?auto=format&fit=crop&w=1920&q=80',
                speed: 0.38,
                direction: 'horizontal',
                opacity: 0.75,
                blendMode: 'screen',
                size: 'cover',
                position: 'center',
            },
            {
                id: 'preset-dust-3',
                name: 'Floating Stardust (Fast / 0.85x)',
                image: 'https://images.unsplash.com/photo-1534447677768-be436bb09401?auto=format&fit=crop&w=1920&q=80',
                speed: 0.85,
                direction: 'reverse',
                opacity: 0.6,
                blendMode: 'screen',
                size: 'cover',
                position: 'center',
            }
        ]
    },
    {
        id: 'mountains',
        name: '🏔️ Mountain & Mist Planes',
        description: 'Atmospheric depth with distant peaks and rolling fog',
        layers: [
            {
                id: 'preset-mnt-1',
                name: 'Distant Peaks (0.15x)',
                image: 'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?auto=format&fit=crop&w=1920&q=80',
                speed: 0.15,
                direction: 'vertical',
                opacity: 1,
                blendMode: 'normal',
                size: 'cover',
                position: 'center',
            },
            {
                id: 'preset-mnt-2',
                name: 'Midground Forest (0.45x)',
                image: 'https://images.unsplash.com/photo-1511497584788-87676104235f?auto=format&fit=crop&w=1920&q=80',
                speed: 0.45,
                direction: 'vertical',
                opacity: 0.85,
                blendMode: 'multiply',
                size: 'cover',
                position: 'center',
            },
            {
                id: 'preset-mnt-3',
                name: 'Drifting Valley Fog (0.9x)',
                image: 'https://images.unsplash.com/photo-1536431311719-398b6704d4cc?auto=format&fit=crop&w=1920&q=80',
                speed: 0.9,
                direction: 'horizontal',
                opacity: 0.55,
                blendMode: 'screen',
                size: 'cover',
                position: 'center',
            }
        ]
    },
    {
        id: 'bokeh',
        name: '✨ Bokeh Glow & Particles',
        description: 'Subtle light play with floating illuminated particles',
        layers: [
            {
                id: 'preset-bokeh-1',
                name: 'Base Ambient (0.2x)',
                image: 'https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=1920&q=80',
                speed: 0.2,
                direction: 'vertical',
                opacity: 0.9,
                blendMode: 'normal',
                size: 'cover',
                position: 'center',
            },
            {
                id: 'preset-bokeh-2',
                name: 'Floating Light Particles (0.75x)',
                image: 'https://images.unsplash.com/photo-1519751138087-5bf79df62d5b?auto=format&fit=crop&w=1920&q=80',
                speed: 0.75,
                direction: 'diagonal',
                opacity: 0.65,
                blendMode: 'screen',
                size: 'cover',
                position: 'center',
            }
        ]
    }
];

export const ParallaxLayersManager = ({
    layers = [],
    onChange, // (newLayers: Array) => void
    title = '🌌 Multi-Layer Parallax Planes',
    subtitle = 'Stack multiple independent background layers, each gliding at its own speed, angle, and blend mode.',
    compact = false
}) => {
    const [expandedLayerIdx, setExpandedLayerIdx] = useState(0);
    const [showPresets, setShowPresets] = useState(false);

    const safeLayers = Array.isArray(layers) ? layers : [];

    const handleAddLayer = () => {
        const nextIdx = safeLayers.length + 1;
        // Default speeds stagger nicely: 0.2, 0.45, 0.75, 1.1...
        const defaultSpeed = Number((0.15 + (safeLayers.length * 0.25)).toFixed(2));
        const newLayer = {
            id: `layer-${Date.now()}-${Math.random().toString(36).substr(2, 4)}`,
            name: `Layer ${nextIdx} (${defaultSpeed}x)`,
            image: '',
            speed: defaultSpeed,
            direction: 'vertical',
            opacity: 1,
            blendMode: safeLayers.length > 0 ? 'screen' : 'normal',
            size: 'cover',
            position: 'center',
            repeat: 'no-repeat',
            zoomMode: 'standard',
        };
        const next = [...safeLayers, newLayer];
        onChange(next);
        setExpandedLayerIdx(next.length - 1);
    };

    const handleUpdateLayer = (idx, field, value) => {
        const next = safeLayers.map((l, i) => {
            if (i !== idx) return l;
            return { ...l, [field]: value };
        });
        onChange(next);
    };

    const handleRemoveLayer = (idx) => {
        const next = safeLayers.filter((_, i) => i !== idx);
        onChange(next);
        if (expandedLayerIdx >= next.length) {
            setExpandedLayerIdx(Math.max(0, next.length - 1));
        }
    };

    const handleMoveLayer = (idx, dir) => {
        const targetIdx = idx + dir;
        if (targetIdx < 0 || targetIdx >= safeLayers.length) return;
        const next = [...safeLayers];
        const [moved] = next.splice(idx, 1);
        next.splice(targetIdx, 0, moved);
        onChange(next);
        setExpandedLayerIdx(targetIdx);
    };

    const handleApplyPreset = (preset) => {
        if (!preset || !preset.layers) return;
        onChange(preset.layers);
        setExpandedLayerIdx(0);
        setShowPresets(false);
    };

    const labelStyle = {
        fontSize: '9px',
        fontWeight: 'bold',
        color: '#64748b',
        display: 'block',
        marginBottom: '2px'
    };

    const inputStyle = {
        width: '100%',
        padding: '4px 6px',
        border: '1px solid #cbd5e1',
        borderRadius: '4px',
        fontSize: '10px',
        background: '#ffffff',
        color: '#1e293b',
        boxSizing: 'border-box'
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {/* Header with Title and Add Button */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <LuLayers size={13} style={{ color: '#2563eb' }} />
                        <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#0f172a' }}>{title}</span>
                        {safeLayers.length > 0 && (
                            <span style={{
                                fontSize: '9px',
                                fontWeight: 'bold',
                                padding: '1px 6px',
                                borderRadius: '10px',
                                background: '#dbeafe',
                                color: '#1d4ed8'
                            }}>
                                {safeLayers.length} {safeLayers.length === 1 ? 'Plane' : 'Planes'}
                            </span>
                        )}
                    </div>
                    {subtitle && (
                        <p style={{ margin: '2px 0 0 0', fontSize: '8.5px', color: '#64748b', lineHeight: '1.3' }}>
                            {subtitle}
                        </p>
                    )}
                </div>

                <div style={{ display: 'flex', gap: '4px' }}>
                    <button
                        type="button"
                        onClick={() => setShowPresets(!showPresets)}
                        style={{
                            padding: '3px 7px',
                            background: showPresets ? '#f1f5f9' : '#f8fafc',
                            color: '#475569',
                            border: '1px solid #cbd5e1',
                            borderRadius: '4px',
                            fontSize: '9.5px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '3px'
                        }}
                        title="Browse 1-Click Multi-Layer Parallax Templates"
                    >
                        <LuSparkles size={11} style={{ color: '#8b5cf6' }} /> Presets
                    </button>
                    <button
                        type="button"
                        onClick={handleAddLayer}
                        style={{
                            padding: '3px 8px',
                            background: '#2563eb',
                            color: '#ffffff',
                            border: 'none',
                            borderRadius: '4px',
                            fontSize: '9.5px',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '3px',
                            boxShadow: '0 1px 2px rgba(37,99,235,0.2)'
                        }}
                        title="Add a new parallax background layer"
                    >
                        <LuPlus size={11} /> + Add Layer
                    </button>
                </div>
            </div>

            {/* Curated Presets Dropdown Drawer */}
            {showPresets && (
                <div style={{
                    background: '#f8fafc',
                    border: '1px solid #cbd5e1',
                    borderRadius: '6px',
                    padding: '8px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px'
                }}>
                    <div style={{ fontSize: '9.5px', fontWeight: 'bold', color: '#334155', display: 'flex', justifyContent: 'space-between' }}>
                        <span>⚡ 1-Click Parallax Depth Profiles:</span>
                        <span onClick={() => setShowPresets(false)} style={{ cursor: 'pointer', color: '#94a3b8' }}>✕ Close</span>
                    </div>
                    {PARALLAX_LAYER_PRESETS.map(preset => (
                        <div
                            key={preset.id}
                            style={{
                                padding: '6px 8px',
                                background: '#ffffff',
                                border: '1px solid #e2e8f0',
                                borderRadius: '4px',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                gap: '8px'
                            }}
                        >
                            <div>
                                <div style={{ fontSize: '10px', fontWeight: 'bold', color: '#1e293b' }}>{preset.name}</div>
                                <div style={{ fontSize: '8.5px', color: '#64748b' }}>{preset.description} ({preset.layers.length} layers)</div>
                            </div>
                            <button
                                type="button"
                                onClick={() => handleApplyPreset(preset)}
                                style={{
                                    padding: '3px 8px',
                                    background: '#7c3aed',
                                    color: '#ffffff',
                                    border: 'none',
                                    borderRadius: '3px',
                                    fontSize: '9px',
                                    fontWeight: 'bold',
                                    cursor: 'pointer',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                Apply
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {/* Empty State */}
            {safeLayers.length === 0 && (
                <div style={{
                    padding: '12px',
                    border: '1px dashed #cbd5e1',
                    borderRadius: '6px',
                    textAlign: 'center',
                    background: '#f8fafc',
                    color: '#64748b',
                    fontSize: '9.5px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '6px'
                }}>
                    <span>No extra parallax layers added yet.</span>
                    <span style={{ fontSize: '8.5px', color: '#94a3b8' }}>
                        Add layers to create rich 2.5D depth planes behind your section content.
                    </span>
                    <button
                        type="button"
                        onClick={handleAddLayer}
                        style={{
                            padding: '4px 10px',
                            background: '#3b82f6',
                            color: '#ffffff',
                            border: 'none',
                            borderRadius: '4px',
                            fontSize: '9.5px',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px'
                        }}
                    >
                        <LuPlus size={11} /> Add First Parallax Layer
                    </button>
                </div>
            )}

            {/* List of Layers */}
            {safeLayers.map((layer, idx) => {
                const isExpanded = expandedLayerIdx === idx;
                const layerSpeed = layer.speed !== undefined && layer.speed !== '' ? parseFloat(layer.speed) : 0.5;
                const isReverse = layer.direction === 'reverse' || layer.direction === 'up';
                const isHoriz = layer.direction?.includes('horizontal');
                const isDiag = layer.direction?.includes('diagonal');

                return (
                    <div
                        key={layer.id || idx}
                        style={{
                            background: '#ffffff',
                            border: isExpanded ? '1.5px solid #3b82f6' : '1px solid #e2e8f0',
                            borderRadius: '6px',
                            overflow: 'hidden',
                            boxShadow: isExpanded ? '0 2px 6px rgba(59,130,246,0.1)' : '0 1px 2px rgba(0,0,0,0.02)',
                            transition: 'all 0.15s ease'
                        }}
                    >
                        {/* Layer Summary Header */}
                        <div
                            onClick={() => setExpandedLayerIdx(isExpanded ? null : idx)}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '6px 8px',
                                background: isExpanded ? '#eff6ff' : '#f8fafc',
                                cursor: 'pointer',
                                userSelect: 'none',
                                borderBottom: isExpanded ? '1px solid #bfdbfe' : 'none'
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
                                {/* Layer Depth Badge */}
                                <span style={{
                                    fontSize: '8.5px',
                                    fontWeight: '800',
                                    background: isExpanded ? '#3b82f6' : '#64748b',
                                    color: '#ffffff',
                                    padding: '1px 5px',
                                    borderRadius: '3px'
                                }}>
                                    #{idx + 1}
                                </span>

                                {/* Layer Thumbnail or Icon */}
                                {layer.image ? (
                                    <img
                                        src={resolveMediaUrl(layer.image)}
                                        alt=""
                                        style={{ width: '20px', height: '16px', objectFit: 'cover', borderRadius: '2px', border: '1px solid #cbd5e1' }}
                                        onError={(e) => { e.target.style.display = 'none'; }}
                                    />
                                ) : (
                                    <LuEye size={12} style={{ color: '#94a3b8' }} />
                                )}

                                <span style={{ fontSize: '10px', fontWeight: 'bold', color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100px' }}>
                                    {layer.name || `Layer ${idx + 1}`}
                                </span>

                                {/* Speed & Direction Pill */}
                                <span style={{
                                    fontSize: '8px',
                                    fontWeight: '700',
                                    color: '#2563eb',
                                    background: '#dbeafe',
                                    padding: '1px 5px',
                                    borderRadius: '3px',
                                    whiteSpace: 'nowrap'
                                }}>
                                    {isReverse ? '▲ ' : isHoriz ? '► ' : isDiag ? '↗ ' : '▼ '}
                                    {layerSpeed}x
                                </span>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }} onClick={(e) => e.stopPropagation()}>
                                <button
                                    type="button"
                                    onClick={() => handleMoveLayer(idx, -1)}
                                    disabled={idx === 0}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        cursor: idx === 0 ? 'default' : 'pointer',
                                        opacity: idx === 0 ? 0.3 : 0.8,
                                        padding: '2px',
                                        color: '#475569'
                                    }}
                                    title="Move deeper behind (lower z-index)"
                                >
                                    <LuChevronUp size={13} />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleMoveLayer(idx, 1)}
                                    disabled={idx === safeLayers.length - 1}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        cursor: idx === safeLayers.length - 1 ? 'default' : 'pointer',
                                        opacity: idx === safeLayers.length - 1 ? 0.3 : 0.8,
                                        padding: '2px',
                                        color: '#475569'
                                    }}
                                    title="Move forward closer to viewer (higher z-index)"
                                >
                                    <LuChevronDown size={13} />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleRemoveLayer(idx)}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        padding: '2px 4px',
                                        color: '#ef4444'
                                    }}
                                    title="Remove this layer"
                                >
                                    <LuTrash2 size={12} />
                                </button>
                            </div>
                        </div>

                        {/* Expanded Layer Controls */}
                        {isExpanded && (
                            <div style={{ padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '10px' }}>
                                {/* Layer Label & Name */}
                                <div style={{ display: 'flex', gap: '6px' }}>
                                    <div style={{ flex: 1 }}>
                                        <label style={labelStyle}>Plane Label / Name</label>
                                        <input
                                            type="text"
                                            value={layer.name || ''}
                                            onChange={(e) => handleUpdateLayer(idx, 'name', e.target.value)}
                                            placeholder={`Layer ${idx + 1}`}
                                            style={inputStyle}
                                        />
                                    </div>
                                    <div style={{ width: '80px' }}>
                                        <label style={labelStyle}>Opacity</label>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            <input
                                                type="number"
                                                min="0"
                                                max="1"
                                                step="0.05"
                                                value={layer.opacity !== undefined ? layer.opacity : 1}
                                                onChange={(e) => handleUpdateLayer(idx, 'opacity', parseFloat(e.target.value))}
                                                style={inputStyle}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Media Input for Layer Image */}
                                <div>
                                    <label style={labelStyle}>Plane Graphic / Media</label>
                                    <MediaInput
                                        size="small"
                                        value={layer.image || ''}
                                        onChange={(e) => {
                                            const val = e?.target ? e.target.value : (typeof e === 'string' ? e : '');
                                            handleUpdateLayer(idx, 'image', val);
                                        }}
                                        placeholder="URL or Upload PNG cutout / background"
                                    />
                                </div>

                                {/* Parallax Speed & Slider */}
                                <div style={{ background: '#f8fafc', padding: '6px 8px', borderRadius: '4px', border: '1px solid #e2e8f0' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                        <label style={{ ...labelStyle, margin: 0, color: '#1e293b' }}>
                                            Parallax Speed Factor
                                        </label>
                                        <span style={{ fontSize: '9px', fontWeight: 'bold', color: '#2563eb', background: '#dbeafe', padding: '1px 6px', borderRadius: '3px' }}>
                                            {layerSpeed}x
                                        </span>
                                    </div>
                                    <input
                                        type="range"
                                        min="-1.0"
                                        max="2.0"
                                        step="0.05"
                                        value={layerSpeed}
                                        onChange={(e) => handleUpdateLayer(idx, 'speed', parseFloat(e.target.value))}
                                        style={{ width: '100%', accentColor: '#2563eb', cursor: 'pointer' }}
                                    />
                                    {/* Quick Speed Presets */}
                                    <div style={{ display: 'flex', gap: '3px', marginTop: '4px' }}>
                                        {[
                                            { label: 'Far (0.15x)', val: 0.15 },
                                            { label: 'Mid (0.4x)', val: 0.4 },
                                            { label: 'Fast (0.8x)', val: 0.8 },
                                            { label: 'Reverse (-0.3x)', val: -0.3 }
                                        ].map(sp => (
                                            <button
                                                key={sp.label}
                                                type="button"
                                                onClick={() => handleUpdateLayer(idx, 'speed', sp.val)}
                                                style={{
                                                    flex: 1,
                                                    padding: '2px 3px',
                                                    fontSize: '8px',
                                                    border: '1px solid #cbd5e1',
                                                    borderRadius: '3px',
                                                    background: layerSpeed === sp.val ? '#eff6ff' : '#ffffff',
                                                    color: layerSpeed === sp.val ? '#1d4ed8' : '#64748b',
                                                    cursor: 'pointer',
                                                    fontWeight: layerSpeed === sp.val ? 'bold' : 'normal'
                                                }}
                                            >
                                                {sp.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Direction & Blend Mode */}
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                                    <div>
                                        <label style={labelStyle}>Motion Direction</label>
                                        <select
                                            value={layer.direction || 'vertical'}
                                            onChange={(e) => handleUpdateLayer(idx, 'direction', e.target.value)}
                                            style={inputStyle}
                                        >
                                            <option value="vertical">Vertical Down (Standard)</option>
                                            <option value="reverse">Vertical Reverse (Ascend)</option>
                                            <option value="horizontal">Horizontal Right Drift</option>
                                            <option value="horizontal-reverse">Horizontal Left Drift</option>
                                            <option value="diagonal">Diagonal Up-Right</option>
                                            <option value="diagonal-up-left">Diagonal Up-Left</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label style={labelStyle}>CSS Blend Mode</label>
                                        <select
                                            value={layer.blendMode || 'normal'}
                                            onChange={(e) => handleUpdateLayer(idx, 'blendMode', e.target.value)}
                                            style={inputStyle}
                                        >
                                            <option value="normal">Normal (Opaque)</option>
                                            <option value="screen">Screen (Light / Mist / Stars)</option>
                                            <option value="overlay">Overlay (High Contrast)</option>
                                            <option value="multiply">Multiply (Dark / Shadow)</option>
                                            <option value="soft-light">Soft Light (Subtle Ambient)</option>
                                            <option value="lighten">Lighten</option>
                                            <option value="color-dodge">Color Dodge (Electric Glow)</option>
                                        </select>
                                    </div>
                                </div>

                                {/* Size & Position */}
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                                    <div>
                                        <label style={labelStyle}>Background Sizing</label>
                                        <select
                                            value={layer.size || 'cover'}
                                            onChange={(e) => handleUpdateLayer(idx, 'size', e.target.value)}
                                            style={inputStyle}
                                        >
                                            <option value="cover">Cover (Fill Canvas)</option>
                                            <option value="contain">Contain (Fit in View)</option>
                                            <option value="auto">Auto (Natural Size)</option>
                                            <option value="100% 100%">100% 100% (Stretch)</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label style={labelStyle}>Position</label>
                                        <select
                                            value={layer.position || 'center'}
                                            onChange={(e) => handleUpdateLayer(idx, 'position', e.target.value)}
                                            style={inputStyle}
                                        >
                                            <option value="center">Center</option>
                                            <option value="top">Top</option>
                                            <option value="bottom">Bottom</option>
                                            <option value="left">Left</option>
                                            <option value="right">Right</option>
                                        </select>
                                    </div>
                                </div>

                                {/* Easing & Zoom Overscan Mode */}
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                                    <div>
                                        <label style={labelStyle}>Easing Smoothing</label>
                                        <select
                                            value={layer.smoothing || 'cubic-bezier(0.16, 1, 0.3, 1)'}
                                            onChange={(e) => handleUpdateLayer(idx, 'smoothing', e.target.value)}
                                            style={inputStyle}
                                        >
                                            <option value="cubic-bezier(0.16, 1, 0.3, 1)">Silky Smooth Spring</option>
                                            <option value="ease-out">Snappy Quick Settle</option>
                                            <option value="ease-in-out">Organic Deceleration</option>
                                            <option value="linear">Linear 1:1 (Zero Lag)</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label style={labelStyle}>Scale & Overscan</label>
                                        <select
                                            value={layer.zoomMode || 'standard'}
                                            onChange={(e) => handleUpdateLayer(idx, 'zoomMode', e.target.value)}
                                            style={inputStyle}
                                        >
                                            <option value="standard">Standard (Safe Edges)</option>
                                            <option value="minimal">Minimal (Low Zoom)</option>
                                            <option value="extra">Extra (High Travel)</option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default ParallaxLayersManager;
